/**
 * Meta Pixel context helpers.
 *
 * The frontend captures the identifiers Meta needs to attribute conversions
 * (`_fbp`, `_fbc`, click ID, user-agent, source URL) at the moment we create
 * a waitlist row. These are forwarded to the backend so it can fire a Lead
 * via the Conversions API later — once the user qualifies — even if the user
 * has already closed the tab.
 *
 * Spec for `_fbc` construction from `fbclid`:
 *   https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc
 *
 * Format: `fb.<subdomainIndex>.<creationTimeMs>.<fbclid>`
 *   - subdomainIndex: 0 = co.uk-style, 1 = root domain, 2 = subdomain.
 *     We treat the current host as a subdomain (2) when more than 2 labels,
 *     otherwise root (1). 0 is reserved for ccTLDs we don't enumerate here.
 */

import { readTouches, type Touch } from '@/analytics/identity'

const FBP_COOKIE = '_fbp'
const FBC_COOKIE = '_fbc'

function readCookie(name: string): string | null {
    if (typeof document === 'undefined') return null
    const match = document.cookie
        .split(';')
        .map(c => c.trim())
        .find(c => c.startsWith(`${name}=`))
    if (!match) return null
    const value = match.slice(name.length + 1)
    return value || null
}

function readQueryParam(name: string): string | null {
    if (typeof window === 'undefined') return null
    try {
        const params = new URLSearchParams(window.location.search)
        const value = params.get(name)
        return value && value.length ? value : null
    } catch {
        return null
    }
}

function subdomainIndexFor(hostname: string | null): number {
    if (!hostname) return 1
    return hostname.split('.').length > 2 ? 2 : 1
}

function fbcFromFbclid(fbclid: string, creationTimeMs: number, hostname: string | null): string {
    return `fb.${subdomainIndexFor(hostname)}.${creationTimeMs}.${fbclid}`
}

function buildFbcFromFbclid(fbclid: string): string {
    const hostname = typeof window === 'undefined' ? null : window.location.hostname
    return fbcFromFbclid(fbclid, Date.now(), hostname)
}

export interface MetaContext {
    /** `_fbp` cookie value, set by the pixel base script. */
    fbp: string | null
    /** `_fbc` cookie value (or constructed from `?fbclid=`). */
    fbc: string | null
    /** Raw fbclid from the URL, if present. */
    fbclid: string | null
    /** Browser user-agent at capture time. */
    user_agent: string | null
    /** Page URL at capture time (used as `event_source_url` in CAPI). */
    event_source_url: string | null
}

/**
 * Snapshots the Meta Pixel attribution context from the current browser
 * tab. Safe to call from SSR (returns nulls when `window`/`document` are
 * unavailable).
 */
export function getMetaContext(): MetaContext {
    const fbp = readCookie(FBP_COOKIE)
    let fbc = readCookie(FBC_COOKIE)
    const fbclid = readQueryParam('fbclid')

    if (!fbc && fbclid) {
        fbc = buildFbcFromFbclid(fbclid)
    }

    return {
        fbp,
        fbc,
        fbclid,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        event_source_url: typeof window !== 'undefined' ? window.location.href : null,
    }
}

export interface CheckoutMetaInputs {
    fbcCookie: string | null
    fbpCookie: string | null
    urlFbclid: string | null
    lastTouch: Pick<Touch, 'fbclid' | 'captured_at'> | null
    firstTouch: Pick<Touch, 'fbclid' | 'captured_at'> | null
    hostname: string | null
    now: number
}

/** Match keys sent with Checkout so the StartTrial webhook can forward them to CAPI. */
export interface CheckoutMetaContext {
    fbc: string | null
    fbp: string | null
}

function fbCookieValue(value: string | null): string | null {
    const trimmed = (value || '').trim()
    return trimmed.startsWith('fb.') ? trimmed : null
}

function fbcFromTouch(
    touch: Pick<Touch, 'fbclid' | 'captured_at'> | null,
    hostname: string | null,
): string | null {
    const fbclid = (touch?.fbclid || '').trim()
    if (!fbclid) return null
    const capturedAt = Date.parse(touch?.captured_at || '')
    if (!Number.isFinite(capturedAt)) return null
    return fbcFromFbclid(fbclid, capturedAt, hostname)
}

/**
 * Picks `fbc`/`fbp` for Checkout. `fbc` comes from the `_fbc` cookie, then
 * the URL `fbclid` (created now), then the stored last or first touch's
 * `fbclid` (created at that touch's `captured_at`). Pure: callers pass the
 * browser state in.
 */
export function resolveCheckoutMetaContext(inputs: CheckoutMetaInputs): CheckoutMetaContext {
    const fbp = fbCookieValue(inputs.fbpCookie)
    let fbc = fbCookieValue(inputs.fbcCookie)
    const urlFbclid = (inputs.urlFbclid || '').trim()
    if (!fbc && urlFbclid) {
        fbc = fbcFromFbclid(urlFbclid, inputs.now, inputs.hostname)
    }
    if (!fbc) fbc = fbcFromTouch(inputs.lastTouch, inputs.hostname)
    if (!fbc) fbc = fbcFromTouch(inputs.firstTouch, inputs.hostname)
    return { fbc, fbp }
}

/** Reads the current tab's Meta match keys for Checkout. Never throws. */
export function getCheckoutMetaContext(): CheckoutMetaContext {
    try {
        let touches: ReturnType<typeof readTouches> = { first: null, last: null }
        try {
            touches = readTouches()
        }
        catch {}
        return resolveCheckoutMetaContext({
            fbcCookie: readCookie(FBC_COOKIE),
            fbpCookie: readCookie(FBP_COOKIE),
            urlFbclid: readQueryParam('fbclid'),
            lastTouch: touches.last,
            firstTouch: touches.first,
            hostname: typeof window === 'undefined' ? null : window.location.hostname,
            now: Date.now(),
        })
    }
    catch {
        return { fbc: null, fbp: null }
    }
}

/**
 * Stable event ID used for client/server Lead deduplication. When the same
 * `eventID` is sent both via the browser pixel and via CAPI, Meta dedupes
 * them as a single Lead.
 */
export function getLeadEventId(waitlistId: string): string {
    return `lead-${waitlistId}`
}

/**
 * Stable event ID for account creation. The same id is used if both the
 * signup form and a later OAuth return try to record the registration, so
 * Meta counts one CompleteRegistration.
 */
export function getRegistrationEventId(userId: string): string {
    return `reg-${userId}`
}

/** How long after `created_at` an auth user still counts as a new account. */
const NEW_ACCOUNT_WINDOW_MS = 5 * 60 * 1000

/**
 * True when this auth user was created moments ago. Used on the OAuth
 * return, where the same `/verify` path also logs in existing accounts.
 */
export function isNewlyCreatedAccount(
    createdAt: string | null | undefined,
    now = Date.now(),
): boolean {
    if (!createdAt) return false
    const created = Date.parse(createdAt)
    if (!Number.isFinite(created)) return false
    const age = now - created
    return age < NEW_ACCOUNT_WINDOW_MS && age > -NEW_ACCOUNT_WINDOW_MS
}
